import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { SelectChangeEvent } from "@mui/material/Select/SelectInput";
import Typography from "@mui/material/Typography";
import React from "react";
import { Controller } from "react-hook-form";

import IconPDF from "../../../../assets/images/icons/icon_pdf.svg";
import { FileContainer } from "../../../../components/FileContainer/FileContainer";
import NextStepDialog from "../../../../components/Modal/NextStepDialog/NextStepDialog";
import { MultiSelect } from "../../../../components/MultiSelect/MultiSelect";
import SingleSelect from "../../../../components/SingleSelect/SingleSelect";
import { DocFile, FolderContentRequestPayload } from "../../../../utils/types/documents.type";
import useDocumentsContext from "../../hooks/useDocumentsContext.hooks";
import useAddTags, { TagType } from "./useAddTags.hooks";

type Props = {
  showDialog?: boolean;
  files?: DocFile[];
  handleClose: () => void;
  reload: Function;
  setErrors: Function;
}

const All_Option = {
  value: "all",
  label: "Select All Investors"
};

export function AddTags ({
  showDialog = false,
  files = [],
  handleClose = () => {},
  reload,
  setErrors,
}: Props ): React.ReactElement {
  const { state } = useDocumentsContext();

  const {
    getFunds,
    getInvestors,
    getQuarters,
    handleSubmit,
    onReview,
    resetForm,
    loading,
    control,
  } = useAddTags(state, { setErrors, handleClose });

  return (
    <NextStepDialog
      open={showDialog}
      onClose={() => {
        resetForm();
        handleClose();
        if(state?.currentFolder?.id) {
          const filters = { ...(state?.selectedFilters || {}) };

          if(state.selectedFilters["funds"].length === state.filtersData["funds"].length) {
            filters.funds = [];
          }
          if(state.selectedFilters["investors"].length === state.filtersData["investors"].length) {
            filters.investors = [];
          }
          if(state.selectedFilters["quarters"].length === state.filtersData["quarters"].length) {
            filters.quarters = [];
          }

          const payload: FolderContentRequestPayload = {
            ...filters,
            ...state.pagination,
            sort: state.sort,
          };

          reload(state?.currentFolder.id, payload);
        }
      }}
      fullWidth={true}
      id="review_uploaded_files"
      actionDisplayType="inline"
      actions={[
        {
          label: "Review Files",
          onClick: handleSubmit((data) => onReview(data, files, () => {
            resetForm();
            handleClose();
            
            if(state?.currentFolder?.id) {
              const filters = state.selectedFilters;

              if(state.selectedFilters["funds"].length === state.filtersData["funds"].length) {
                filters.funds = [];
              }
              if(state.selectedFilters["investors"].length === state.filtersData["investors"].length) {
                filters.investors = [];
              }
              if(state.selectedFilters["quarters"].length === state.filtersData["quarters"].length) {
                filters.quarters = [];
              }

              const payload: FolderContentRequestPayload = {
                ...filters,
                ...state.pagination,
                searchText: state.selectedFilters.searchText,
                sort: state.sort,
              };
              
              reload(state?.currentFolder.id, payload, false);
            }
          })),
          id: "review_files_button",
          variant: "contained",
          color: "primary",
          disabled: loading,
        },
      ]}
      title="Add Tags to Files"
    >
      <Box flex={1}>
        <Grid container rowSpacing={2} px={2}>
          <Grid item xs={12} pt={0}>
            <FileContainer
              icon={IconPDF}
              showUploadProgress={false}
              styles={{ marginTop: 0 }}
              hideClear={true}
              file={files}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant={"h4"}>
              Apply to all files... (optional)
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="investor"
              render={({
                field: {
                  onChange,
                  value
                },
                fieldState: {
                  error
                }
              }) => {
                return (<SingleSelect
                    id="select_investors"
                    disabled={loading}
                    optionList={[All_Option, ...getInvestors()]}
                    label="Investor"
                    fullWidth={true}
                    idField="value"
                    labelField="label"
                    handleOptionSelection={(event: SelectChangeEvent) => onChange(event.target.value)}
                    value={value ?? ''}
                    error={!!error}
                    noGutter={true}
                    helperText={error?.message ?? ''}
                  />
                );
              }}
              control={control}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="funds"
              render={({
                field: {
                  onChange,
                  value
                },
                fieldState: {
                  error
                }
              }) => {
                return (
                  <MultiSelect
                    disabled={loading}
                    items={getFunds().map((fund: any) => ({
                      ...fund,
                      selected: value?.includes(fund.id),
                    }))}
                    fullWidth={true}
                    noGutter={true}
                    id="select_funds"
                    label="Funds"
                    onChange={(items) => {
                      const itemIDs: Array<string> = [];

                      items.forEach(item => item.selected && itemIDs.push(item.id));

                      onChange(itemIDs); 
                    }}
                    error={!!error}
                    helperText={error?.message ?? ''}
                  />
                );
              }}
              control={control}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="quarter"
              render={({
                field: {
                  onChange,
                  value
                },
                fieldState: {
                  error
                }
              }) => {
                return (<SingleSelect
                    id="select_quarter"
                    noGutter={true}
                    disabled={loading}
                    optionList={getQuarters()}
                    label="Quarter"
                    fullWidth={true}
                    idField="value"
                    labelField="label"
                    handleOptionSelection={(event: SelectChangeEvent) => { onChange(event.target.value); }}
                    value={value ?? ''}
                    error={!!error}
                    helperText={error?.message ?? ''}
                  />
                );
              }}
              control={control}
            />
          </Grid>
        </Grid>
      </Box>
    </NextStepDialog>
  );
}